import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { addDays, startOfWeek } from "date-fns";
import { PAGE_LIMIT_MAX, instantAtFacilityTime, shiftDay, toFacilityDate } from "@alm/domain";
import { Button } from "@/components/ui/button";
import { AppointmentEditorDialog } from "@/components/app/appointments/appointment-editor-dialog";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { ReportTable } from "@/components/app/documents/report-table";
import { useFacility } from "@/lib/facility/facility";
import { useZoneFor } from "@/lib/facility/zone";
import { CalendarPlus } from "@/lib/icons";
import { useCanAnywhere, useOptionalPolicy } from "@/lib/policy";
import { useCalendarAnchor } from "@/hooks/use-calendar-anchor";
import { useSearchParamText } from "@/hooks/use-search-param-text";
import {
  optionalDay,
  optionalOneOf,
  optionalString,
  optionalTrue,
  searchPatch,
  optionalSearch,
} from "@/lib/search";
import { cn } from "@/lib/utils";
import { orpc } from "@/lib/orpc";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ANY, TransportFilters, type TransportFilterState } from "./-components/transport-filters";
import {
  filtersFromSearch,
  searchFromFilters,
  type TransportSearch,
} from "./-lib/transport-search";
import { transportColumns, type Appointment } from "./-components/transport-columns";
import { TransportMonth } from "./-components/transport-month";
import { ymd, type Trip } from "./-components/transport-trip";
import { TransportWeek } from "./-components/transport-week";
import { tripActionsColumn } from "./-components/trip-actions";
import { TransportCard } from "./-components/transport-card";
import { CancelAppointmentDialog } from "@/components/app/appointments/cancel-appointment-dialog";
import { keepWhileSame } from "@/lib/query/keep-while-same";

const strings = (value: unknown): string[] | undefined => {
  const list = Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];
  return list.length > 0 ? list : undefined;
};

export const Route = createFileRoute("/_authenticated/_support/transport/")({
  validateSearch: (search: Record<string, unknown>): TransportSearch => ({
    view: optionalOneOf(["week", "month"], search.view),
    q: optionalSearch(search.q),
    types: strings(search.types),
    statuses: strings(search.statuses),
    by: optionalString(search.by) || undefined,
    mobility: optionalString(search.mobility) || undefined,
    rides: optionalTrue(search.rides),
    unassigned: optionalTrue(search.unassigned),
    driver: optionalString(search.driver) || undefined,
    from: optionalDay(search.from),
    to: optionalDay(search.to),
    on: optionalDay(search.on),
  }),
  component: Transport,
});

/** A date box can hold a five-digit year mid-typing; anything not YYYY-MM-DD falls back. */
const dayOr = (value: string, fallback: string) =>
  /^\d{4}-\d{2}-\d{2}$/.test(value) && value >= "1900" ? value : fallback;

/** Everybody who has to be driven somewhere, in the order the day happens. */
function Transport() {
  const { facilityId } = useFacility();
  // The building's diary, so the building's clock, even for a manager whose own profile is
  // elsewhere.
  const zoneFor = useZoneFor();
  const zone = useOptionalPolicy()?.me?.facilityTimezones?.[facilityId] ?? zoneFor(facilityId);
  const today = toFacilityDate(new Date(), zone);
  /* Uses `useCanAnywhere`: a floor manager holds `schedule:manage` per building and nothing at the
   * organization, and `can()` without a facility answers only for the organization. */
  const canManage = useCanAnywhere("appointment:manage");
  const [booking, setBooking] = useState(false);
  const [assigning, setAssigning] = useState<Appointment | null>(null);
  const [cancelling, setCancelling] = useState<Appointment | null>(null);
  const search = Route.useSearch();
  const patch = searchPatch(useNavigate({ from: Route.fullPath }));
  const refine = (next: TransportSearch) => patch({ ...next });
  const { text, change: setText, settling } = useSearchParamText(search.q, (q) => refine({ q }));
  const filters = filtersFromSearch(search, text, today);
  const setFilters = (next: TransportFilterState) => {
    if (next.search !== text) setText(next.search);
    refine(searchFromFilters(next, today));
  };
  const view = search.view ?? "list";
  const setView = (next: "list" | "week" | "month") =>
    refine({ view: next === "list" ? undefined : next });
  const { monthStart, weekStart, setMonthStart, setWeekStart } = useCalendarAnchor(
    search.on,
    (on) => refine({ on }),
  );

  /* The calendars name their own range. Asking the server for the filters'
     two dates and then drawing a month of them is how a month view ends up
     showing one day's trips in the first cell and nothing anywhere else. */
  const range =
    view === "week"
      ? { from: ymd(weekStart), to: ymd(addDays(weekStart, 6)) }
      : view === "month"
        ? {
            from: ymd(startOfWeek(monthStart, { weekStartsOn: 1 })),
            to: ymd(addDays(startOfWeek(monthStart, { weekStartsOn: 1 }), 41)),
          }
        : { from: dayOr(filters.from, today), to: dayOr(filters.to, dayOr(filters.from, today)) };

  const columns = transportColumns(zone);

  const board = useQuery({
    ...orpc.care.appointments.list.queryOptions({
      input: {
        facilityId,
        // Whole days on the building's clock: UTC midnight is 7 PM in Texas, which moved evening
        // appointments onto the next day.
        from: instantAtFacilityTime(range.from, "00:00", zone).toISOString(),
        to: new Date(
          instantAtFacilityTime(shiftDay(range.to, 1), "00:00", zone).getTime() - 1,
        ).toISOString(),
        limit: PAGE_LIMIT_MAX,
        offset: 0,
        ...(search.q ? { search: search.q } : {}),
        ...(filters.types.length > 0 ? { types: filters.types } : {}),
        ...(filters.statuses.length > 0 ? { statuses: filters.statuses } : {}),
        ...(filters.transportBy === ANY ? {} : { transportBy: filters.transportBy }),
        ...(filters.mobilityNeed === ANY ? {} : { mobilityNeed: filters.mobilityNeed }),
        ...(filters.ridesOnly ? { transportRequired: true } : {}),
        ...(filters.unassignedDriver ? { unassignedDriver: true } : {}),
        ...(filters.driverUserId === ANY ? {} : { driverUserId: filters.driverUserId }),
      },
    }),
    placeholderData: keepWhileSame({ facilityId }),
  });
  const searching = settling || board.isPlaceholderData;

  return (
    <>
      <PageHeader
        title="Appointments"
        meta="Ordered by pickup time, because that is the order the day happens in."
        actions={
          canManage ? (
            <Button onClick={() => setBooking(true)}>
              <CalendarPlus className="size-4" />
              Add appointment
            </Button>
          ) : null
        }
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Tabs value={view} onValueChange={(next) => setView(next as "list" | "week" | "month")}>
          <TabsList>
            <TabsTrigger value="list">List</TabsTrigger>
            <TabsTrigger value="week">Week</TabsTrigger>
            <TabsTrigger value="month">Month</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <TransportFilters filters={filters} onChange={setFilters} hideDates={view !== "list"} />

      <div
        aria-busy={searching || undefined}
        className={cn("space-y-4 transition-opacity", searching && "opacity-60")}
      >
        {board.isError ? (
          <ReadFailed
            message="Couldn't load the transport board."
            error={board.error}
            onRetry={() => void board.refetch()}
            retrying={board.isFetching}
          />
        ) : view === "week" ? (
          <TransportWeek
            weekStart={weekStart}
            setWeekStart={setWeekStart}
            trips={(board.data?.rows ?? []) as Trip[]}
            pending={board.isPending}
            zone={zone}
          />
        ) : view === "month" ? (
          <TransportMonth
            monthStart={monthStart}
            setMonthStart={setMonthStart}
            trips={(board.data?.rows ?? []) as Trip[]}
            pending={board.isPending}
            zone={zone}
            /* Clicking a day is how somebody gets from "that one is busy" to the
             rows behind it, which is the only question a month cell raises. */
            onPickDay={(day) => {
              refine({ from: ymd(day), to: ymd(day), view: undefined });
            }}
          />
        ) : (
          <ReportTable
            rows={board.data?.rows}
            pending={board.isPending}
            filename={`transport-${filters.from}.csv`}
            rowKey={(row) => row.id}
            noun="appointments"
            renderCard={(row) =>
              canManage ? (
                <TransportCard
                  row={row}
                  zone={zone}
                  onAssign={setAssigning}
                  onCancel={setCancelling}
                />
              ) : (
                <TransportCard row={row} zone={zone} />
              )
            }
            columns={
              canManage ? [...columns, tripActionsColumn(setAssigning, setCancelling)] : columns
            }
            empty={{
              title: "Nothing on the board",
              description: "Appointments matching these filters appear here.",
            }}
          />
        )}
      </div>

      {/* A ride that is missing from the board is booked from the board, rather
          than by going to find whose ride it is first. */}
      {assigning ? (
        <AppointmentEditorDialog
          residentId={assigning.residentId}
          facilityId={facilityId}
          zone={zone}
          editing={assigning}
          duplicating={null}
          onClose={() => setAssigning(null)}
        />
      ) : null}
      {cancelling ? (
        <CancelAppointmentDialog appointment={cancelling} onClose={() => setCancelling(null)} />
      ) : null}
      {booking ? (
        <AppointmentEditorDialog
          residentId={null}
          facilityId={facilityId}
          zone={zone}
          editing={null}
          duplicating={null}
          defaults={{ day: range.from, transportRequired: true }}
          onClose={() => setBooking(false)}
        />
      ) : null}
    </>
  );
}
